import { useMemo, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ChevronLeft, Plus, Edit, Trash2 } from 'lucide-react'
import type { AssetSeverity } from '../types'
import { ResponsiveContainer, PieChart, Pie, Cell, Label, Tooltip, Legend } from 'recharts'
import { useScansStore } from '../stores/scansStore'
import { useAuthStore } from '../stores/authStore'
import { projectAssetsApi } from '../services/projectAssetsApi'

const COLORS: Record<AssetSeverity, string> = {
  Critical: '#C00000',
  High: '#FF0000',
  Medium: '#FFC000',
  Low: '#00B050',
  Info: '#00B0F0'
}

// Mock vulnerability breakdown data (this could be replaced with real API data later)
const generateMockBreakdown = (): Array<{ name: AssetSeverity, value: number }> => {
  const severities: AssetSeverity[] = ['Critical', 'High', 'Medium', 'Low', 'Info']
  return severities.map(name => ({ name, value: Math.floor(Math.random() * 20) }))
}


const AssetDetails = () => {
  const { projectId = '', assetId = '' } = useParams()
  const navigate = useNavigate()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingScan, setEditingScan] = useState<any>(null)
  const [asset, setAsset] = useState<any>(null)
  const [assetLoading, setAssetLoading] = useState(true)
  const [assetError, setAssetError] = useState<string | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    type: 'MANUAL' as const,
    status: 'PENDING' as const,
    testingType: 'BLACK_BOX' as const,
    description: ''
  })

  const { user } = useAuthStore()
  const { 
    scans, 
    loading: scansLoading, 
    error: scansError, 
    fetchScans, 
    createScan, 
    updateScan, 
    deleteScan, 
    clearError 
  } = useScansStore()
  
  // Check if user has admin role (only ADMIN can manage scans)
  const canManageScans = user?.roles?.includes('ADMIN')

  // Generate vulnerability breakdown from real data
  const breakdown = useMemo(() => {
    if (!asset?.vulnerabilityCounts?.severityCounts) {
      return generateMockBreakdown()
    }
    
    const severityCounts = asset.vulnerabilityCounts.severityCounts
    return [
      { name: 'Critical' as AssetSeverity, value: severityCounts.critical || 0 },
      { name: 'High' as AssetSeverity, value: severityCounts.high || 0 },
      { name: 'Medium' as AssetSeverity, value: severityCounts.medium || 0 },
      { name: 'Low' as AssetSeverity, value: severityCounts.low || 0 },
      { name: 'Info' as AssetSeverity, value: severityCounts.info || 0 }
    ] // Show all severities, even with 0 values
  }, [asset])

  const total = useMemo(() => breakdown.reduce((s, i) => s + i.value, 0), [breakdown])

  // If no vulnerabilities, show a single "No Vulnerabilities" segment
  const chartData = useMemo(() => {
    if (total === 0) {
      return [{ name: 'No Vulnerabilities', value: 1 }]
    }
    return breakdown
  }, [breakdown, total])

  // Fetch asset details when component mounts
  useEffect(() => {
    const fetchAsset = async () => {
      if (assetId) {
        setAssetLoading(true)
        setAssetError(null)
        try {
          const response = await projectAssetsApi.getAssetDetails(assetId)
          if (response.success && response.data) {
            setAsset(response.data)
          } else {
            setAssetError('Failed to fetch asset details')
          }
        } catch (error: any) {
          setAssetError(error.message || 'Failed to fetch asset details')
        } finally {
          setAssetLoading(false)
        }
      }
    }

    fetchAsset()
  }, [assetId])

  // Fetch scans when component mounts
  useEffect(() => {
    if (projectId && assetId) {
      fetchScans({ assetId })
    }
  }, [fetchScans, projectId, assetId])

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      case 'IN_PROGRESS': return 'bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200'
      case 'COMPLETED': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'FAILED': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      case 'CANCELLED': return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
    }
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'MANUAL': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
      case 'AUTOMATED': return 'bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200'
      case 'WEB_APPLICATION': return 'bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200'
      case 'MOBILE_APPLICATION': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'API': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
      case 'DESKTOP_APPLICATION': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
      case 'DATABASE': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const openCreateModal = () => {
    setEditingScan(null)
    setFormData({
      name: '',
      type: 'MANUAL',
      status: 'PENDING',
      testingType: 'BLACK_BOX',
      description: ''
    })
    setShowCreateModal(true)
  }

  const openEditModal = (scan: any) => {
    setEditingScan(scan)
    setFormData({
      name: scan.name,
      type: scan.type,
      status: scan.status,
      testingType: scan.testingType,
      description: scan.description
    })
    setShowCreateModal(true)
  }

  const closeModals = () => {
    setShowCreateModal(false)
    setEditingScan(null)
    setFormData({
      name: '',
      type: 'MANUAL',
      status: 'PENDING',
      testingType: 'BLACK_BOX',
      description: ''
    })
  }

  const handleCreateScan = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Use the asset's tenant ID
    const tenantId = asset?.tenantId
    if (!tenantId) {
      console.error('Asset tenant ID is not available.')
      alert('Cannot create scan: Asset tenant information is missing.')
      return
    }
    
    try {
      await createScan({ 
        ...formData, 
        projectId: projectId!, 
        tenantId, 
        assetId: assetId! 
      })
      closeModals()
    } catch (err) {
      console.error('Failed to create scan:', err)
    }
  }

  const handleUpdateScan = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingScan) return
    try {
      await updateScan(editingScan._id, formData)
      closeModals()
    } catch (err) {
      console.error('Failed to update scan:', err)
    }
  }

  const handleDeleteScan = async (scanId: string) => {
    if (window.confirm('Are you sure you want to delete this scan?')) {
      try {
        await deleteScan(scanId)
      } catch (err) {
        console.error('Failed to delete scan:', err)
      }
    }
  }

  const CustomTooltip = ({ active, payload }: { active?: boolean, payload?: any[] }) => {
    if (!active || !payload || payload.length === 0) return null
    const item = payload[0]
    const name = item?.payload?.name as AssetSeverity
    const value = item?.value as number
    const color = COLORS[name]
    return (
      <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-md dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100">
        <div className="flex items-center gap-2">
          <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: color }} />
          <span className="font-medium">{name}</span>
          <span className="ml-2 text-gray-500 dark:text-gray-400">{value}</span>
        </div>
      </div>
    )
  }

  if (assetLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (assetError) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Link to={`/projects/${projectId}`} className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white">
            <ChevronLeft className="h-4 w-4 mr-1" /> Back to Assets
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Asset Details</h1>
          <div />
        </div>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
              <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                <p>{assetError}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!asset) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Link to={`/projects/${projectId}`} className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white">
            <ChevronLeft className="h-4 w-4 mr-1" /> Back to Assets
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Asset Details</h1>
          <div />
        </div>
        <div className="text-center py-12">
          <div className="text-gray-500 dark:text-gray-400">
            <svg className="mx-auto h-12 w-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Asset not found</h3>
            <p className="text-gray-500 dark:text-gray-400">The requested asset could not be found.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Link to={`/projects/${projectId}`} className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white">
          <ChevronLeft className="h-4 w-4 mr-1" /> Back to Assets
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{asset.name || 'Asset Details'}</h1>
        <div />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Asset details */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 lg:col-span-2">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Asset Details</h2>
          <div className="space-y-4 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <div className="text-base font-semibold text-gray-900 dark:text-white">{asset.name || 'Unknown Asset'}</div>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                getTypeColor(asset?.assetType)
              }`}>{asset?.assetType?.replace('_', ' ') || 'Unknown'}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <div className="text-gray-500 dark:text-gray-400">Description</div>
                <div className="text-gray-900 dark:text-white">{asset.description || 'No description'}</div>
              </div>
              <div>
                <div className="text-gray-500 dark:text-gray-400">Type</div>
                <div className="text-gray-900 dark:text-white">{asset.assetType?.replace('_', ' ') || 'Unknown'}</div>
              </div>
              <div>
                <div className="text-gray-500 dark:text-gray-400">Created On</div>
                <div className="text-gray-900 dark:text-white">{asset.createdAt ? formatDate(asset.createdAt) : 'Unknown'}</div>
              </div>
              <div>
                <div className="text-gray-500 dark:text-gray-400">Updated On</div>
                <div className="text-gray-900 dark:text-white">{asset.updatedAt ? formatDate(asset.updatedAt) : 'Unknown'}</div>
              </div>
              <div>
                <div className="text-gray-500 dark:text-gray-400">Scans</div>
                <div className="text-gray-900 dark:text-white">{scans.length}</div>
              </div>
              {asset.webUrl && (
                <div>
                  <div className="text-gray-500 dark:text-gray-400">Web URL</div>
                  <div className="text-gray-900 dark:text-white">
                    <a href={asset.webUrl} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300">
                      {asset.webUrl}
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right: Small donut chart */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Vulnerabilities</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={chartData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={90} paddingAngle={2} stroke="#ffffff" strokeWidth={2}>
                  {chartData.map((entry) => (
                    <Cell key={`cell-${entry.name}`} fill={entry.name === 'No Vulnerabilities' ? '#e5e7eb' : COLORS[entry.name as AssetSeverity]} />
                  ))}
                  <Label position="center" content={({ viewBox }) => {
                    if (!viewBox || typeof viewBox !== 'object' || !('cx' in viewBox) || !('cy' in viewBox)) return null
                    const { cx, cy } = viewBox as { cx: number, cy: number }
                    return (
                      <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central">
                        <tspan className="fill-gray-900 dark:fill-white" fontSize="18" fontWeight={600}>{total}</tspan>
                        <tspan x={cx} dy="16" className="fill-gray-500 dark:fill-gray-400" fontSize="10">Total</tspan>
                      </text>
                    )
                  }} />
                </Pie>
                <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} content={<CustomTooltip />} />
                <Legend
                  verticalAlign="bottom"
                  height={40}
                  formatter={(value: string, entry: any) => {
                    const count = entry?.payload?.value as number
                    return (
                      // eslint-disable-next-line react/no-unknown-property
                      <span className="text-gray-900 dark:text-gray-100">
                        {value}
                        <span className="ml-1 text-gray-500 dark:text-gray-400">
                          ({value === 'No Vulnerabilities' ? '0' : count})
                        </span>
                      </span>
                    )
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Scans table */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Scans</h2>
          <div className="flex items-center space-x-4">
            <div className="text-sm text-gray-500 dark:text-gray-400">{scans.length} total</div>
            {canManageScans && (
              <button
                onClick={openCreateModal}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200 dark:bg-primary-900 dark:text-primary-200 dark:hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Scan
              </button>
            )}
          </div>
        </div>

        {/* Error Message */}
        {scansError && (
          <div className="px-6 py-4 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
                <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                  <p>{scansError}</p>
                </div>
                <div className="mt-4">
                  <button
                    onClick={clearError}
                    className="text-sm bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200 px-3 py-1 rounded-md hover:bg-red-200 dark:hover:bg-red-700"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {scansLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : scans.length === 0 ? (
          <div className="p-6">
            <div className="text-center py-12">
              <div className="text-gray-500 dark:text-gray-400">
                <svg className="mx-auto h-12 w-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No scans found</h3>
                {canManageScans && (
                  <p className="text-gray-500 dark:text-gray-400">Create your first security scan to get started.</p>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Testing Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Created At</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {scans.map(scan => (
                  <tr 
                    key={scan._id} 
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                    onClick={() => navigate(`/projects/${projectId}/assets/${assetId}/scans/${scan._id}`)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {scan.name}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate">
                        {scan.description}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(scan.type)}`}>
                        {scan.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(scan.status)}`}>
                        {scan.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {scan.testingType.replace('_', ' ')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(scan.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        {canManageScans && (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                openEditModal(scan)
                              }}
                              className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDeleteScan(scan._id)
                              }}
                              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingScan) && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-6 border w-full max-w-2xl shadow-2xl rounded-lg bg-white dark:bg-gray-800">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-6">
                {editingScan ? 'Edit Scan' : 'Create New Scan'}
              </h3>

              <form onSubmit={editingScan ? handleUpdateScan : handleCreateScan}>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Scan Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                      placeholder="Enter scan name"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Scan Type *
                      </label>
                      <select
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                        required
                      >
                        <option value="MANUAL">Manual</option>
                        <option value="AUTOMATED">Automated</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Status *
                      </label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                        required
                      >
                        <option value="PENDING">Pending</option>
                        <option value="IN_PROGRESS">In Progress</option>
                        <option value="COMPLETED">Completed</option>
                        <option value="FAILED">Failed</option>
                        <option value="CANCELLED">Cancelled</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Testing Type *
                    </label>
                    <select
                      value={formData.testingType}
                      onChange={(e) => setFormData({ ...formData, testingType: e.target.value as any })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                      required
                    >
                      <option value="BLACK_BOX">Black Box</option>
                      <option value="GRAY_BOX">Gray Box</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Description *
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                      rows={4}
                      placeholder="Enter scan description..."
                      required
                    />
                  </div>
                </div>

                {/* Navigation Buttons */}
                <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={closeModals}
                    className="px-6 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-3 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors shadow-sm"
                  >
                    {editingScan ? 'Update Scan' : 'Create Scan'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AssetDetails


